import type { Credential } from "../types";

const SECTION_ELEMENT_ID_PREFIX = "credential-section-";

/** The DOM id a category's section renders with. Shared by the section list
 * and the section nav so scroll-to and scroll-spy always agree on the anchor. */
export function credentialSectionElementId(categorySlug: string): string {
  return `${SECTION_ELEMENT_ID_PREFIX}${categorySlug}`;
}

/**
 * A credential needs attention when something is actually owed on it.
 *
 * Two cases, and only two:
 * - **Required and unsatisfied.** Neither that credential nor an accepted
 *   alternative is on file.
 * - **Held and lapsing** — expiring soon or already expired — whatever its
 *   requirement. A licence that has run out is a problem even where nobody
 *   demanded it, because the record now asserts something untrue.
 *
 * An OPTIONAL credential nobody supplied is deliberately not attention. It was
 * counted before, and since most of the catalogue is optional the badges added
 * up to far more than the required total above them: sections reading "4 need
 * attention" against a header of seven required credentials, describing a debt
 * nobody owed. The card still shows "Missing / Optional" — the fact stays, the
 * demand goes.
 *
 * Not-applicable credentials never count; nothing is owed for them either way.
 */
export function countCredentialsNeedingAttention(
  credentials: Credential[],
): number {
  const { expired, missing, expiring } =
    summarizeCredentialAttention(credentials);
  return expired + missing + expiring;
}

/** What is outstanding in a group, split by kind. */
export type CredentialAttention = {
  /** Held, and the date on it has passed. */
  expired: number;
  /** Required, with neither the credential nor an accepted alternative on file. */
  missing: number;
  /** Held, and inside the warning window. */
  expiring: number;
};

/**
 * The same three kinds the record's own header counts, per group.
 *
 * A section heading reading "4 need attention" says only that work exists, and
 * the four possible reasons are not equally urgent: an expired licence is a
 * person who cannot work today, a missing optional document is not. The reader
 * had to open the section to find out which they were looking at — on a page
 * whose sections are closed by default, that is every section, every time.
 *
 * Counted the same way `countCredentialsNeedingAttention` decides attention at
 * all — required-and-absent, or held-and-lapsing whatever the requirement — so
 * the parts always add up to the number the nav rail shows.
 */
export function summarizeCredentialAttention(
  credentials: Credential[],
): CredentialAttention {
  const attention: CredentialAttention = {
    expired: 0,
    missing: 0,
    expiring: 0,
  };
  for (const credential of credentials) {
    if (credential.requirement === "not-applicable") {
      continue;
    }
    if (credential.status === "missing") {
      // The server resolves accepted alternatives; keep the missing row factual
      // without counting an identification requirement that is already met.
      if (
        credential.requirement === "required" &&
        !credential.requirementSatisfiedBy
      ) {
        attention.missing += 1;
      }
      continue;
    }
    if (credential.expiryStatus === "expired") {
      attention.expired += 1;
    } else if (credential.expiryStatus === "expiring") {
      attention.expiring += 1;
    }
  }
  return attention;
}
